import { useEffect, useMemo, useState } from "react";
import {
  AppstoreOutlined,
  BookOutlined,
  CustomerServiceOutlined,
  SafetyCertificateOutlined,
  TruckOutlined
} from "@ant-design/icons";
import { Empty } from "antd";

import axiosInstance from "../../utils/axios";
import {
  readHomeCache,
  writeHomeCache
} from "../../utils/homeCache";
import {
  getAllIndexPage,
  getRandomIndexPage
} from "../../api/category";
import { getAllBlog } from "../../api/blog";

import ItemProduct from "../../section/itemProduct";
import ItemProductGrid from "../../section/itemProductGrid";
import ItemBlog from "../../section/itemBlog";

import HomeIsMobile from "./homeIsMobile";
import { useDevice } from "../../hooks";

import "./homeRedesign.css";
import "./homeFull.css";

const normalize = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const isBookSection = (section) => {
  const text = normalize(
    [
      section?.category_index_name,
      ...(section?.product || [])
        .slice(0, 6)
        .map((item) => item?.product_name)
    ].join(" ")
  );

  return (
    text.includes("sach") ||
    text.includes("tai lieu") ||
    text.includes("book") ||
    text.includes("cam nang")
  );
};


const isLikelyBookProduct = (item) => {
  const text = normalize(
    [
      item?.product_name,
      item?.product_slug
    ].join(" ")
  );

  return (
    text.includes("sach") ||
    text.includes("tai lieu") ||
    text.includes("cam nang") ||
    text.includes("giao trinh") ||
    text.includes("ebook") ||
    text.includes("textbook")
  );
};

const uniqueProducts = (items = []) => {
  const seen = new Set();

  return items.filter((item) => {
    const key =
      item?.product_id ||
      item?.product_slug ||
      item?.product_name;

    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const uniqueCategories = (items = []) => {
  const seen = new Set();

  return items.filter((item) => {
    const label =
      shortCategoryName(
        item?.category_name
      );

    const key = normalize(label);

    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const isPromotionSection = (section) => {
  const text = normalize(section?.category_index_name);

  return (
    text.includes("khuyen mai") ||
    text.includes("giam gia") ||
    text.includes("uu dai") ||
    text.includes("sale")
  );
};

const shortCategoryName = (name = "") => {
  const n = normalize(name);

  if (n.includes("dinh duong")) return "Dinh dưỡng";
  if (n.includes("phuc hoi")) return "Phục hồi chức năng";
  if (n.includes("dung cu y khoa")) return "Dụng cụ y khoa";
  if (n.includes("vat tu")) return "Vật tư y tế";
  if (n.includes("thiet bi")) return "Thiết bị y tế";
  if (n.includes("nguoi lon tuoi")) return "Người lớn tuổi";

  if (n.includes("sach") || n.includes("tai lieu")) {
    return "Sách & tài liệu";
  }

  if (
    n.includes("voucher") ||
    n.includes("khuyen mai") ||
    n.includes("ma giam")
  ) {
    return "Khuyến mãi";
  }

  return name;
};


const newestTime = (item) => {
  const raw =
    item?.create_at ||
    item?.created_at ||
    item?.createdAt ||
    item?.update_at ||
    item?.updated_at ||
    item?.updatedAt;

  if (!raw) return 0;

  const time = new Date(raw).getTime();

  return Number.isFinite(time)
    ? time
    : 0;
};

const newestProducts = (items = []) =>
  [...items].sort((a, b) => {
    const dateDiff =
      newestTime(b) -
      newestTime(a);

    if (dateDiff !== 0) {
      return dateDiff;
    }

    return (
      Number(b?.product_id || 0) -
      Number(a?.product_id || 0)
    );
  });

const newestBlogs = (items = []) =>
  [...items].sort((a, b) => {
    const dateDiff =
      newestTime(b) -
      newestTime(a);

    if (dateDiff !== 0) {
      return dateDiff;
    }

    return (
      Number(b?.blog_id || 0) -
      Number(a?.blog_id || 0)
    );
  });

const bannerImage = (banner) =>
  banner?.index_page_image || "";

const bannerLink = (banner) =>
  banner?.index_page_link || "/danh-muc-san-pham";

function Banner({ banner, className = "" }) {
  if (!bannerImage(banner)) return null;

  return (
    <a
      href={bannerLink(banner)}
      className={`cyk-admin-banner ${className}`}
    >
      <img
        src={bannerImage(banner)}
        alt="Y KHOA"
      />
    </a>
  );
}

export default function Home() {
  const { isMobile } = useDevice();

  const [category, setCategory] = useState(
    () => readHomeCache("category", [])
  );
  const [categoryIndex, setCategoryIndex] = useState(
    () => readHomeCache("categoryIndex", [])
  );
  const [remoteBanner, setRemoteBanner] = useState(
    () => readHomeCache("remoteBanner", [])
  );
  const [randomIndex, setRandomIndex] = useState(
    () => readHomeCache("randomIndexDesktop", [])
  );
  const [blog, setBlog] = useState(
    () => readHomeCache("blog", [])
  );
  const [itemsToShow, setItemsToShow] = useState(12);
  const [saleClock, setSaleClock] = useState("00:00:00");
  const [fullFlashSale, setFullFlashSale] = useState(
    () => readHomeCache("fullFlashSale", [])
  );

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      const end = new Date(now);

      end.setHours(23, 59, 59, 999);

      const diff = Math.max(
        0,
        end.getTime() - now.getTime()
      );

      const hours =
        Math.floor(diff / 3600000);

      const minutes =
        Math.floor(
          (diff % 3600000) / 60000
        );

      const seconds =
        Math.floor(
          (diff % 60000) / 1000
        );

      const pad = (value) =>
        String(value).padStart(2, "0");

      setSaleClock(
        `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
      );
    };

    updateClock();

    const timer =
      window.setInterval(
        updateClock,
        1000
      );

    return () =>
      window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let active = true;

    const applyCache = (
      key,
      setter,
      value
    ) => {
      if (!active) return;

      setter(value);
      writeHomeCache(key, value);
    };

    axiosInstance
      .get("/category/getAllHome")
      .then((res) => {
        applyCache(
          "category",
          setCategory,
          res?.data || { data: [] }
        );
      })
      .catch(() => {});

    axiosInstance
      .get("/index-page/getAllIndexPage")
      .then((res) => {
        applyCache(
          "remoteBanner",
          setRemoteBanner,
          res?.data?.data || []
        );
      })
      .catch(() => {});

    getAllIndexPage(
      (value) => {
        applyCache(
          "categoryIndex",
          setCategoryIndex,
          value
        );
      }
    );

    axiosInstance
      .get(
        "/category-index/getByCategoryIndexId/2?page=1&pageSize=100"
      )
      .then((res) => {
        const rows =
          Array.isArray(res?.data?.data)
            ? res.data.data
            : [];

        applyCache(
          "fullFlashSale",
          setFullFlashSale,
          rows
        );
      })
      .catch(() => {});

    getAllBlog(
      "page=1&pageSize=8",
      (value) => {
        applyCache(
          "blog",
          setBlog,
          value
        );
      }
    );

    return () => {
      active = false;
    };
  }, []);


  useEffect(() => {
    let active = true;

    getRandomIndexPage(
      `page=1&pageSize=${itemsToShow}`,
      (value) => {
        if (!active) return;

        setRandomIndex(value);

        writeHomeCache(
          "randomIndexDesktop",
          value
        );
      }
    );

    return () => {
      active = false;
    };
  }, [itemsToShow]);

  const categories = useMemo(
    () => [...(category?.data || [])],
    [category]
  );

  const categoriesDisplay = useMemo(
    () =>
      uniqueCategories(categories)
        .slice(0, 8),
    [categories]
  );

  const sections = useMemo(
    () => [...(categoryIndex?.data || [])],
    [categoryIndex]
  );

  const heroBanners = useMemo(
    () =>
      remoteBanner.filter(
        (item) =>
          (item?.index_page_position || "hero") === "hero" &&
          Number(item?.index_page_visible ?? 1) !== 0 &&
          bannerImage(item)
      ),
    [remoteBanner]
  );

  const promoBanners = useMemo(
    () =>
      remoteBanner.filter(
        (item) =>
          item?.index_page_position === "promo" &&
          Number(item?.index_page_visible ?? 1) !== 0 &&
          bannerImage(item)
      ),
    [remoteBanner]
  );

  const topDealSection = useMemo(
    () =>
      sections.find((section) => {
        const name = normalize(
          section?.category_index_name
        );

        return (
          name.includes("top deal") ||
          name.includes("topdeal") ||
          Number(section?.category_index_id) === 1
        );
      }),
    [sections]
  );

  const flashSaleSection = useMemo(
    () =>
      sections.find((section) => {
        const name = normalize(
          section?.category_index_name
        );

        return (
          name.includes("flash sale") ||
          name.includes("flashsale") ||
          Number(section?.category_index_id) === 2
        );
      }),
    [sections]
  );

  const allSectionProducts = useMemo(
    () =>
      uniqueProducts(
        sections.flatMap(
          (section) =>
            section?.product || []
        )
      ),
    [sections]
  );

  const topDealProducts = useMemo(
    () =>
      uniqueProducts(
        topDealSection?.product || []
      ).slice(0, 12),
    [topDealSection]
  );

  const flashSaleProducts = useMemo(
    () =>
      uniqueProducts(
        fullFlashSale.length > 0
          ? fullFlashSale
          : (flashSaleSection?.product || [])
      ).slice(0, 100),
    [fullFlashSale, flashSaleSection]
  );

  const latestProducts = useMemo(
    () =>
      newestProducts(
        allSectionProducts
      ).slice(0, 12),
    [allSectionProducts]
  );

  const latestBlogs = useMemo(
    () =>
      newestBlogs(
        blog?.data || []
      ).slice(0, 8),
    [blog]
  );

  if (isMobile) {
    return <HomeIsMobile />;
  }

  return (
    <main className="cyk-home cyk-full-home">
      <div className="cyk-full-shell">

        {heroBanners.length > 0 && (
          <section className="cyk-full-hero">
            <Banner
              banner={heroBanners[0]}
              className="cyk-full-hero-banner"
            />

            {heroBanners.length > 1 && (
              <div className="cyk-full-hero-side">
                {heroBanners.slice(1, 3).map((item) => (
                  <Banner
                    key={item.index_page_id}
                    banner={item}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        <section className="cyk-full-categories">
          <div className="cyk-full-heading">
            <div>
              <span>DANH MỤC</span>
              <h2>Chăm sóc sức khỏe toàn diện</h2>
            </div>

            <a href="/danh-muc-san-pham">
              Xem tất cả →
            </a>
          </div>

          <div className="cyk-full-category-grid">
            {categoriesDisplay.map((item, index) => (
              <a
                key={item?.category_slug || index}
                href={`/danh-muc-san-pham/${item?.category_slug}`}
                className="cyk-full-category"
              >
                <div>
                  {item?.category_image ? (
                    <img
                      src={item.category_image}
                      alt={item?.category_name}
                      loading="lazy"
                    />
                  ) : (
                    <BookOutlined />
                  )}
                </div>

                <strong>
                  {shortCategoryName(item?.category_name)}
                </strong>
              </a>
            ))}
          </div>
        </section>

        <section className="cyk-full-trust">
          <div>
            <SafetyCertificateOutlined />
            <span>
              <strong>100% chính hãng</strong>
              <small>Nguồn gốc rõ ràng</small>
            </span>
          </div>

          <div>
            <TruckOutlined />
            <span>
              <strong>Giao hàng nhanh</strong>
              <small>Phục vụ toàn quốc</small>
            </span>
          </div>

          <div>
            <CustomerServiceOutlined />
            <span>
              <strong>Hỗ trợ 24/7</strong>
              <small>Tư vấn tận tâm</small>
            </span>
          </div>
        </section>

        {promoBanners.length > 0 && (
          <section className="cyk-full-promo">
            {promoBanners.map((item) => (
              <Banner
                key={item.index_page_id}
                banner={item}
              />
            ))}
          </section>
        )}

        {topDealProducts.length > 0 && (
          <section className="cyk-full-section">
            <div className="cyk-full-heading">
              <div>
                
                <h2>Sản phẩm nổi bật</h2>
              </div>

              <a
                href={`/danh-muc-trang-chu/${topDealSection?.category_index_id}`}
              >
                Xem tất cả →
              </a>
            </div>

            <div className="cyk-horizontal-products">
              <ItemProduct
                product={topDealProducts}
                grid={5}
              />
            </div>
          </section>
        )}

        {flashSaleProducts.length > 0 && (
          <section className="cyk-full-section cyk-flash-section">
            <div className="cyk-full-heading cyk-flash-heading">
              <div>
                

                <div className="cyk-flash-title-line">
                  <h2>Khuyến mãi hôm nay</h2>

                  <span className="cyk-flash-clock">
                    <span>◷</span>
                    {saleClock}
                  </span>
                </div>
              </div>

              <a
                href={`/danh-muc-trang-chu/${flashSaleSection?.category_index_id}`}
              >
                Xem tất cả →
              </a>
            </div>

            <div className="cyk-horizontal-products">
              <ItemProduct
                product={flashSaleProducts}
                grid={5}
              />
            </div>
          </section>
        )}

        {latestProducts.length > 0 && (
          <section className="cyk-full-section">
            <div className="cyk-full-heading">
              <div>
                
                <h2>Gợi ý hôm nay</h2>
              </div>

              <a href="/san-pham-moi">
                Xem tất cả →
              </a>
            </div>

            <div className="cyk-horizontal-products">
              <ItemProductGrid
                product={latestProducts}
                grid={5}
              />
            </div>
          </section>
        )}

        <section className="cyk-full-section">
          <div className="cyk-full-heading">
            <div>
              
              <h2>Bài viết mới nhất</h2>
            </div>

            <a href="/blog">
              Xem tất cả →
            </a>
          </div>

          {latestBlogs.length > 0 ? (
            <ItemBlog
              blog={latestBlogs}
            />
          ) : (
            <Empty />
          )}
        </section>

        <section className="cyk-full-newsletter">
          <div>
            <span>Y KHOA</span>
            <h2>Nhận thông tin sản phẩm & ưu đãi mới</h2>
            <p>
              Theo dõi các chương trình mới và kiến thức
              chăm sóc sức khỏe từ Cửa Hàng Y Khoa.
            </p>
          </div>

          <a href="/dang-ky">
            Đăng ký tài khoản →
          </a>
        </section>

      </div>
    </main>
  );
}
